import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Sparkles, TrendingUp, Smile, Meh, Frown, Laugh, Leaf } from "lucide-react";

const CONF = { high: "bg-sage/15 text-sage", medium: "bg-primary/15 text-primary", low: "bg-muted text-muted-foreground" };
const THEME_COLORS = [
  "bg-rose-500/10 text-rose-600", "bg-amber-500/10 text-amber-600", "bg-emerald-500/10 text-emerald-600",
  "bg-blue-500/10 text-blue-600", "bg-violet-500/10 text-violet-600", "bg-teal-500/10 text-teal-600",
];

export default function Insights() {
  const { t } = useI18n();
  const [period, setPeriod] = useState("weekly");
  const [insights, setInsights] = useState([]);
  const [themes, setThemes] = useState([]);
  const [trend, setTrend] = useState([]);
  const [today, setToday] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const load = (p) => {
    setLoading(true);
    api.get("/insights", { params: { period: p } })
      .then(({ data }) => {
        setInsights(data.insights || []);
        setThemes(data.themes || []);
        setTrend(data.mood_trend || []);
        setToday(data.today_mood || null);
        setMsg(data.message || "");
      })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(period); }, [period]);

  const moodMeta = (m) => {
    if (m == null) return { label: "—", Icon: Meh, bg: "bg-muted", text: "text-muted-foreground" };
    if (m <= 3) return { label: t("insights.moodLow"), Icon: Frown, bg: "bg-rose-500/10", text: "text-rose-500" };
    if (m <= 5) return { label: t("insights.moodOkay"), Icon: Meh, bg: "bg-amber-500/10", text: "text-amber-500" };
    if (m <= 7) return { label: t("insights.moodGood"), Icon: Smile, bg: "bg-emerald-500/10", text: "text-emerald-600" };
    return { label: t("insights.moodHappy"), Icon: Laugh, bg: "bg-emerald-500/10", text: "text-emerald-600" };
  };
  const tm = moodMeta(today?.mood);

  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("insights.title")}</h1>
      <p className="mt-2 max-w-lg text-muted-foreground">{t("insights.subtitle")}</p>

      <Tabs value={period} onValueChange={setPeriod} className="mt-6">
        <TabsList className="rounded-full bg-secondary p-1" data-testid="insights-period-tabs">
          {["daily", "weekly", "monthly", "yearly"].map((p) => (
            <TabsTrigger key={p} value={p} className="rounded-full capitalize" data-testid={`period-${p}`}>{t(`insights.${p}`)}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading && <div className="mt-8 h-8 w-8 animate-breathe rounded-full bg-primary" />}

      {!loading && trend.length > 1 && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="ambient-shadow rounded-3xl border border-border/60 bg-card p-6" data-testid="insights-today-mood">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{today?.is_today ? t("insights.todayTitle") : t("insights.latestTitle")}</p>
            <div className={`mt-4 flex h-14 w-14 items-center justify-center rounded-2xl ${tm.bg} ${tm.text}`}>
              <tm.Icon className="h-7 w-7" strokeWidth={1.5} />
            </div>
            <div className="mt-4 font-serif text-3xl">{tm.label}</div>
            <div className="text-sm text-muted-foreground">
              {today?.mood != null ? `${today.mood}/10` : ""}
              {today?.energy != null ? ` · ${t("checkin.energy")} ${today.energy}/10` : ""}
            </div>
            {today?.date && <div className="mt-1 text-xs text-muted-foreground/70" data-testid="insights-mood-date">{today.date}</div>}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.06 }}
            className="ambient-shadow rounded-3xl border border-border/60 bg-card p-6 lg:col-span-2" data-testid="insights-mood-trend">
            <h2 className="font-serif text-xl">{t("insights.moodTrend")}</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("insights.moodTrendSub")}</p>
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                  <Line type="monotone" dataKey="mood" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="energy" stroke="hsl(var(--sage))" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>
      )}

      {!loading && themes.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="ambient-shadow mt-6 rounded-3xl border border-border/60 bg-card p-6" data-testid="insights-themes">
          <div className="flex items-center gap-2">
            <Leaf className="h-4 w-4 text-sage" strokeWidth={1.5} />
            <h2 className="font-serif text-xl">{t("insights.themes")}</h2>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("insights.themesSub")}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {themes.map((th, i) => (
              <span key={i} data-testid={`theme-pill-${i}`} className={`rounded-full px-3.5 py-1.5 text-sm font-medium ${THEME_COLORS[i % THEME_COLORS.length]}`}>{th}</span>
            ))}
          </div>
        </motion.div>
      )}

      <div className="mt-6 space-y-4">
        {!loading && insights.length > 0 && <h2 className="font-serif text-xl" data-testid="insights-personal-heading">{t("insights.personal")}</h2>}
        {!loading && insights.length === 0 && (
          <div className="rounded-3xl border border-border/60 bg-card p-8 text-center ambient-shadow">
            <Sparkles className="mx-auto h-8 w-8 text-primary" strokeWidth={1.5} />
            <p className="mt-4 text-muted-foreground">{msg || t("insights.empty")}</p>
          </div>
        )}
        {insights.map((ins, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.08 }}
            className="ambient-shadow rounded-3xl border border-border/60 bg-card p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10"><TrendingUp className="h-4 w-4 text-primary" strokeWidth={1.5} /></div>
              <div className="flex-1">
                <p className="font-serif text-lg leading-snug">{ins.text}</p>
                {ins.why && <p className="mt-2 text-sm text-muted-foreground"><span className="font-medium text-foreground/70">{t("insights.why")} </span>{ins.why}</p>}
                <Badge className={`mt-3 rounded-full ${CONF[ins.confidence] || CONF.low}`}>{t(`insights.${ins.confidence}`, ins.confidence || "low")} {t("insights.confidence")}</Badge>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
